import React, { useEffect, useState } from 'react'
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    LinearProgress,
    TextField,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'

import { axiosInstance } from '../../../../helpers'
import { getWorkbookBucketLabel } from '../../../ApplicationWorkbook/workbookCatalog'
import { WorkbookBucketId } from '../../../ApplicationWorkbook/workbook.types'

interface WorkbookApplicationResponse {
    status: string
    progress: {
        percent: number
        identity_complete: boolean
        workbook_complete: boolean
        buckets: Array<{
            bucket_id: WorkbookBucketId
            required_total: number
            required_filled: number
            complete: boolean
        }>
    }
}

const STATUS_LABELS: Record<string, string> = {
    not_started: 'Not started',
    in_progress: 'In progress',
    identity_complete: 'Identity complete',
    complete: 'Complete',
    waived_by_pm: 'Waived by PM',
}

const useStyles = makeStyles((theme: Theme) => ({
    bucketRow: {
        marginBottom: theme.spacing(2),
    },
    bucketHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: theme.spacing(0.5),
    },
    overrideRow: {
        display: 'flex',
        flexDirection: 'column',
        gap: theme.spacing(1),
        marginTop: theme.spacing(2),
    },
}))

interface LeadWorkbookProgressPanelProps {
    leadId: number
}

export const LeadWorkbookProgressPanel: React.FC<LeadWorkbookProgressPanelProps> = ({
    leadId,
}) => {
    const classes = useStyles()
    const [
        application,
        setApplication,
    ] = useState<WorkbookApplicationResponse | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [overrideReason, setOverrideReason] = useState('')
    const [overriding, setOverriding] = useState(false)

    const loadApplication = async () => {
        setLoading(true)
        setError('')
        try {
            const {
                data,
            } = await axiosInstance.get<WorkbookApplicationResponse>(
                `outreach/leads/${leadId}/application/`,
            )
            setApplication(data)
        } catch {
            setError('Unable to load application workbook progress.')
            setApplication(null)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        loadApplication()
    }, [leadId])

    const handleWaive = async () => {
        setOverriding(true)
        setError('')
        try {
            const {
                data,
            } = await axiosInstance.post<WorkbookApplicationResponse>(
                `outreach/leads/${leadId}/application/override/`,
                { reason: overrideReason },
            )
            setApplication(data)
            setOverrideReason('')
        } catch {
            setError('Unable to waive workbook requirements.')
        } finally {
            setOverriding(false)
        }
    }

    if (loading) {
        return (
            <Box display="flex" alignItems="center" py={2}>
                <CircularProgress size={20} />
                <Typography variant="body2" style={{ marginLeft: 8 }}>
                    Loading workbook progress…
                </Typography>
            </Box>
        )
    }

    if (!application) {
        return (
            <Typography variant="body2" color="textSecondary">
                {error || 'No application workbook started yet.'}
            </Typography>
        )
    }

    const statusLabel = STATUS_LABELS[application.status] ?? application.status
    const canWaive =
        application.status !== 'complete' &&
        application.status !== 'waived_by_pm'

    return (
        <Box>
            <Box display="flex" alignItems="center" mb={2} style={{ gap: 8 }}>
                <Typography variant="subtitle2">Workbook status</Typography>
                <Chip size="small" label={statusLabel} color="primary" />
            </Box>

            <Typography variant="body2" color="textSecondary" gutterBottom>
                Overall progress: {application.progress.percent}%
            </Typography>
            <LinearProgress
                variant="determinate"
                value={application.progress.percent}
                style={{ marginBottom: 16, height: 8, borderRadius: 4 }}
            />

            {application.progress.buckets.map((bucket) => {
                const label = getWorkbookBucketLabel(bucket.bucket_id)
                const pct =
                    bucket.required_total > 0
                        ? Math.round(
                              (bucket.required_filled / bucket.required_total) *
                                  100,
                          )
                        : 100
                return (
                    <Box key={bucket.bucket_id} className={classes.bucketRow}>
                        <Box className={classes.bucketHeader}>
                            <Typography
                                variant="body2"
                                style={{ fontWeight: 600 }}
                            >
                                {label}
                            </Typography>
                            <Chip
                                size="small"
                                label={
                                    bucket.complete
                                        ? 'Complete'
                                        : `${bucket.required_filled}/${bucket.required_total}`
                                }
                                style={{
                                    backgroundColor: bucket.complete
                                        ? '#d1fae5'
                                        : '#f3f4f6',
                                    color: bucket.complete
                                        ? '#047857'
                                        : '#6b7280',
                                }}
                            />
                        </Box>
                        <LinearProgress
                            variant="determinate"
                            value={pct}
                            style={{ height: 6, borderRadius: 3 }}
                        />
                    </Box>
                )
            })}

            {canWaive && (
                <Box className={classes.overrideRow}>
                    <Typography variant="subtitle2">PM override</Typography>
                    <TextField
                        label="Reason (optional)"
                        value={overrideReason}
                        onChange={(e) => setOverrideReason(e.target.value)}
                        fullWidth
                        size="small"
                        variant="outlined"
                    />
                    <Button
                        variant="outlined"
                        color="secondary"
                        disabled={overriding}
                        onClick={handleWaive}
                    >
                        {overriding
                            ? 'Waiving…'
                            : 'Waive workbook requirements'}
                    </Button>
                </Box>
            )}

            {error && (
                <Typography
                    variant="body2"
                    color="error"
                    style={{ marginTop: 8 }}
                >
                    {error}
                </Typography>
            )}
        </Box>
    )
}
